import * as cookieManager from 'js-cookie';
import { numberUtils } from '@origin-digital/utils-toolbox';

import { USER_REGION } from '../constants/cookie-names';
import HOUSEHOLD_SIZES from '../constants/household-sizes';
import * as REGIONS from '../constants/regions';
import { DIFFERENTIATOR_TYPES } from '../constants/differentiator-types';
import DEFAULTS from '../constants/default-state-values';

export const regionsList = Object.values(REGIONS);

/**
 * Returns window.location.pathname
 * @returns {?String}
 */
export function getLocationPathName() {
  return window.location.pathname;
}

/**
 * Return the current region that we think the user is in. Most of the time this
 * will be an Australian state or territory, but in the future this could be
 * non-AU states (or even subdivisions of states, like regional-QLD vs
 * non-regional-QLD).
 *
 * Returns null if the application cannot determine which region the user is
 * visiting from.
 *
 * @returns {?Region}
 */
function getRegion(cookies = null) {
  const stateInCookie = cookies.get(USER_REGION);
  if (!stateInCookie) {
    return null;
  }
  const stateId = stateInCookie.toUpperCase();
  return regionsList.find((region) => region.id === stateId) || null;
}

/**
 * Change the region that the application believes the user is visiting from.
 * Currently this means overriding a site-wide cookie, but that is the source of
 * truth for this property about the user.
 *
 * @param {Region.id} regionId
 */
function setRegion(regionId = null) {
  if (!regionId) {
    throw new ReferenceError('Missing required parameter regionId');
  }
  const matchedRegion = regionsList.find((region) => region.id === regionId);
  if (!matchedRegion) {
    throw new RangeError(
      `Unknown regionId value ${regionId}. Valid values are: ${JSON.stringify(
        regionsList.map((region) => region.id),
      )}`,
    );
  }
  if (typeof window.setTargetStateCookie === 'function') {
    window.setTargetStateCookie(matchedRegion.userRegionCookieValue);
  }

  if (typeof window._dtmLayer !== 'undefined') {
    window._dtmLayer.state = matchedRegion.userRegionCookieValue;
  }
}

/**
 * Get the connection scenario of the user based on if they are moving or if they are a Origin customer
 * @param {boolean} movingCustomer - the customer is moving house
 * @param {boolean} originCustomer - is a Origin customer
 */
export function getConnectionScenario(movingCustomer, originCustomer, isRenew) {
  if (movingCustomer && originCustomer) {
    return DIFFERENTIATOR_TYPES.CUST_MOVE;
  } else if (!movingCustomer && originCustomer) {
    if (isRenew) {
      return DIFFERENTIATOR_TYPES.CUST_PRCH;
    }
    return DIFFERENTIATOR_TYPES.CUST_SWT;
  } else if (!movingCustomer && !originCustomer) {
    return DIFFERENTIATOR_TYPES.PROS_SWT;
  } else {
    return DIFFERENTIATOR_TYPES.PROS_MOVE;
  }
}

/**
 * Object composer function. Returns an object with setters/getters to/from ContextHub.
 * Mutates value accordingly.
 *
 * @param {SessionPersistence}
 * @param {cookieManager}
 */
export function createFilterStateHelper(
  sessionPersistence = null,
  cookies = cookieManager,
  locationPathName = getLocationPathName,
) {
  if (!sessionPersistence) {
    throw new ReferenceError(
      'createFilterStateHelper: Missing required parameter `sessionPersistence`',
    );
  }

  if (!cookies) {
    throw new ReferenceError('createFilterStateHelper: Missing required parameter `cookieManager`');
  }

  return {
    get postcode() {
      const planSearchPostcode = sessionPersistence.getItem('plansearchpostcode');
      if (planSearchPostcode) {
        return planSearchPostcode;
      }
      // TODO: should we check the dtmstate in CH?
      const storedRegion = getRegion(cookies) || DEFAULTS.region;
      const matchedRegion = regionsList.find((region) => region.id === storedRegion.id);
      return matchedRegion.defaultPostcode;
    },

    set postcode(newVal) {
      sessionPersistence.setItem('plansearchpostcode', newVal);
      sessionPersistence.setItem('postCode', newVal);
      if (typeof window._dtmLayer !== 'undefined') {
        window._dtmLayer.postcode = newVal;
      }
    },

    get servicesRequired() {
      const returnValue = {
        ...DEFAULTS.servicesRequired,
      };
      let planFuel = sessionPersistence.getItem('planfuel');
      if (planFuel && typeof planFuel === 'string') {
        planFuel = planFuel.toLowerCase();
        returnValue.electricity = planFuel.includes('elec');
        returnValue.gas = planFuel.includes('gas');
      }
      return returnValue;
    },

    set servicesRequired(newVal) {
      const electricityValue = newVal.electricity === true ? 'Elec' : '';
      const gasValue = newVal.gas === true ? 'Gas' : '';
      const delimiter = electricityValue && gasValue ? '_' : '';
      sessionPersistence.setItem('planfuel', electricityValue + delimiter + gasValue);
    },

    get hasSolarPanels() {
      const hasSolarPanels = sessionPersistence.getItem('plansolar');
      return hasSolarPanels === 'Yes';
    },

    set hasSolarPanels(newVal) {
      newVal = newVal === true ? 'Yes' : 'No';
      sessionPersistence.setItem('plansolar', newVal);
    },

    get isCustomerMoving() {
      const movingCustomer = sessionPersistence.getItem('movingCustomer');
      return movingCustomer === 'Yes';
    },

    set isCustomerMoving(newVal) {
      sessionPersistence.setItem('movingCustomer', newVal === true ? 'Yes' : 'No');
      sessionPersistence.setItem('iWantTo', newVal === true ? 'iwt0b' : 'iwt0a');
    },

    get isCustomer() {
      const originCustomer = sessionPersistence.getItem('originCustomer');
      return originCustomer === 'Yes';
    },

    set isCustomer(newVal) {
      newVal = newVal === true ? 'Yes' : 'No';
      sessionPersistence.setItem('originCustomer', newVal);
    },

    get connectionScenario() {
      let connectionScenario = sessionPersistence.getItem('connectionScenario');
      if (!connectionScenario) {
        const movingCustomer = sessionPersistence.getItem('movingCustomer');
        const originCustomer = sessionPersistence.getItem('originCustomer');

        connectionScenario = getConnectionScenario(
          movingCustomer === 'Yes',
          originCustomer === 'Yes',
        );
      }
      return connectionScenario;
    },

    set connectionScenario(newVal) {
      sessionPersistence.setItem('connectionScenario', newVal);
    },

    get isBusiness() {
      const pathname = locationPathName();
      if (pathname && pathname.indexOf('/business/') !== -1) {
        return true;
      } else {
        return DEFAULTS.isBusiness;
      }
    },
    set isBusiness(newVal) {
      const plancustomertype = newVal ? 'business' : 'residential';

      sessionPersistence.setItem('plancustomertype', plancustomertype);
    },

    get renewableEnergy() {
      const greenOptionsSelected = sessionPersistence.getItem('greenOptionsSelected') || {};
      return {
        electricity: convertGreenPercentageFromContextHubFormat(greenOptionsSelected['01']),
        gas: convertGreenPercentageFromContextHubFormat(greenOptionsSelected['02']),
      };
    },

    set renewableEnergy(newVal) {
      const greenOptionsSelected = {
        '01': convertGreenPercentageToContextHubFormat(newVal.electricity),
        '02': convertGreenPercentageToContextHubFormat(newVal.gas),
      };
      sessionPersistence.setItem('greenOptionsSelected', greenOptionsSelected);
    },

    get populateConnectionScenario() {
      const prepopulateConnectionScenario = sessionPersistence.getItem('step1Submitted');
      return prepopulateConnectionScenario === true;
    },

    set populateConnectionScenario(newVal) {
      //The context hub item step1Submitted is the key used to prepopulate connection scenario
      sessionPersistence.setItem('step1Submitted', newVal);
    },

    get iWantTo() {
      return sessionPersistence.getItem('iWantTo');
    },

    set iWantTo(newVal) {
      sessionPersistence.setItem('iWantTo', newVal);
    },

    get movingCustomer() {
      return sessionPersistence.getItem('movingCustomer');
    },

    set movingCustomer(newVal) {
      return sessionPersistence.setItem('movingCustomer', newVal ? 'Yes' : 'No');
    },
  };
}
/**
 * Object composer function. Returns an object with setters/getters to/from ContextHub.
 * Mutates value accordingly.
 *
 * @param {SessionPersistence}
 */
export function createPlanStateHelper(sessionPersistence = null) {
  if (!sessionPersistence) {
    throw new ReferenceError(
      'createPlanStateHelper: Missing required parameter `sessionPersistence`',
    );
  }

  return {
    get title() {
      return sessionPersistence.getItem('planTitle');
    },
    set title(value) {
      sessionPersistence.setItem('planTitle', value);
    },
    get description() {
      return sessionPersistence.getItem('planIdentifier');
    },
    set description(value) {
      sessionPersistence.setItem('planIdentifier', value);
    },
    get bonusOffer() {
      return sessionPersistence.getItem('planBonusOffer');
    },
    set bonusOffer(value) {
      sessionPersistence.setItem('planBonusOffer', value);
    },
    get fuel() {
      return sessionPersistence.getItem('products');
    },
    set fuel(fuel) {
      let divisionIds = [];
      let campaignId;

      const products = Object.keys(fuel).map((key) => {
        if (fuel[key].divisionId) {
          divisionIds.push(fuel[key].divisionId);
        }

        if (!campaignId && fuel[key].campaignId) {
          campaignId = fuel[key].campaignId;
        }

        return {
          divisionId: fuel[key].divisionId,
          sapProductId: fuel[key].productCode,
          campaignId: fuel[key].campaignId,
          productId: fuel[key].productId,
          productTitle: fuel[key].title,
          productDescription: fuel[key].discountText,
          isSolarCampaign: fuel[key].isFromSolarCampaign,
          solarFitText: fuel[key].solarFitText,
          solarFeedInTariff: fuel[key].solarFit,
          productTermsConditions: fuel[key].termsAndConditions,
          productExtraTerms: fuel[key].additionalTerms,
          productDiscount: fuel[key].discount
            ? numberUtils.toFixedDecimalPlace(fuel[key].discount, 0)
            : null,
          productDiscountText: fuel[key].discountText,
          isCrossSellItem: fuel[key].isCrossSellItem,
          isEmailBillingMandatory: fuel[key].isEmailBillingMandatory,
          isEmailCorrespondenceMandatory: fuel[key].isEmailCorrespondenceMandatory,
          isDirectDebitMandatory: fuel[key].isDirectDebitMandatory,
          fixedRebate: fuel[key].fixedRebate,
        };
      });
      // Add divisionId for analytics = AG-1108
      sessionPersistence.setItem('divisionId', divisionIds.join(','));
      // Add campaignId for analytics = AG-1108
      sessionPersistence.setItem('campaignId', campaignId);
      sessionPersistence.setItem('products', products);
    },
    get plansApiEndPoint() {
      return sessionPersistence.getItem('plansApiEndPoint');
    },
    set plansApiEndPoint(value) {
      sessionPersistence.setItem('plansApiEndPoint', value);
    },
  };
}

/**
 * Object composer function. Returns an object with setters/getters to/from ContextHub.
 * Mutates value accordingly.
 *
 * @param {SessionPersistence}
 */
export function createUserStateHelper(
  sessionPersistence = null,
  cookies = cookieManager,
  locationPathName = getLocationPathName,
) {
  if (!sessionPersistence) {
    throw new ReferenceError(
      'createUserStateHelper: Missing required parameter `sessionPersistence`',
    );
  }
  if (!cookies) {
    throw new ReferenceError('createUserStateHelper: Missing required parameter `cookieManager`');
  }
  return {
    get isResidential() {
      const pathname = locationPathName();
      if (pathname && pathname.indexOf('/business/') !== -1) {
        return false;
      } else {
        return DEFAULTS.isResidential;
      }
    },
    set isResidential(newVal) {
      const plancustomertype = newVal === true ? 'residential' : 'business';
      const customerType = newVal === true ? '0001' : '0002';

      sessionPersistence.setItem('plancustomertype', plancustomertype);
      sessionPersistence.setItem('customerType', customerType);
    },

    get isBusiness() {
      const pathname = locationPathName();
      if (pathname && pathname.indexOf('/business/') !== -1) {
        return true;
      } else {
        return DEFAULTS.isBusiness;
      }
    },
    set isBusiness(newVal) {
      const plancustomertype = newVal ? 'business' : 'residential';
      const customerType = newVal === true ? '0002' : '0001';

      sessionPersistence.setItem('plancustomertype', plancustomertype);
      sessionPersistence.setItem('customerType', customerType);
    },

    get userRegion() {
      const regionCookie = getRegion(cookies);
      const regionSessionStorage = sessionPersistence.getItem('dtmstate');
      if (typeof regionSessionStorage === 'string') {
        return regionSessionStorage;
      }
      if (regionCookie && typeof regionCookie.id === 'string') {
        return regionCookie.id.toUpperCase();
      }

      return null;
    },

    set userRegion(newVal) {
      setRegion(newVal, cookies);
      sessionPersistence.setItem('dtmstate', newVal.toUpperCase());
    },

    get entryPage() {
      return sessionPersistence.getItem('entryPage') || DEFAULTS.entryPage;
    },

    set entryPage(newVal) {
      sessionPersistence.setItem('entryPage', DEFAULTS.entryPage);
    },

    get entryPageUrl() {
      return window.location.href;
    },

    set entryPageUrl(newVal) {
      sessionPersistence.setItem('entryPageUrl', window.location.href);
    },

    get userAddressId() {
      return sessionPersistence.getItem('addressId') || DEFAULTS.addressId;
    },

    set userAddressId(newVal) {
      sessionPersistence.setItem('addressId', newVal);
    },

    get userDPID() {
      return sessionPersistence.getItem('connectionAddressDPID') || DEFAULTS.connectionAddressDPID;
    },

    set userDPID(newVal) {
      sessionPersistence.setItem('connectionAddressDPID', newVal);
    },
  };
}

/**
 * Object composer function. Returns an object with setters/getters to/from ContextHub.
 * Mutates value accordingly.
 *
 * @param {SessionPersistence}
 */
export function createTrackingStateHelper(sessionPersistence = null) {
  if (!sessionPersistence) {
    throw new ReferenceError(
      'createPlanStateHelper: Missing required parameter `sessionPersistence`',
    );
  }

  return {
    get comparePlansProducts() {
      return sessionPersistence.getItem('comparePlansProducts');
    },
    set comparePlansProducts(value) {
      sessionPersistence.setItem('comparePlansProducts', value);
    },
  };
}

/**
 * Get green percentage value from percentage string eg 100, 50 (context hub format)
 *
 * @param percentage - string to be converted to GreenPercentage
 * @returns GreenPercentage that string represents otherwise 0.00
 */
const convertGreenPercentageFromContextHubFormat = (percentage) => {
  switch (percentage) {
    case 0:
      return '0.00';
    case 25:
      return '0.25';
    case 50:
      return '0.50';
    case 100:
      return '1.00';
    default:
      return '0.00';
  }
};

/**
 * Convert green percentage value from the string in app to eg 100, 50 (context hub format)
 *
 * @param percentage - string to be converted to GreenPercentage
 * @returns GreenPercentage that string represents otherwise 0.00
 */
const convertGreenPercentageToContextHubFormat = (percentage) => {
  switch (percentage) {
    case '0.00':
      return 0;
    case '0.25':
      return 25;
    case '0.50':
      return 50;
    case '1.00':
      return 100;
    default:
      return 0;
  }
};
